import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as qs from 'utils/queryParams';
import { encodeQueryParams, SerializerTypes } from './paramsSerializer';
import { decodeValue, filterNullOrEmpty, UpdateType, updateUrlSearch } from './useQueryParam';

// Thanks to our inspiration: https://github.com/pbeshai/use-query-params

/**
 * This hook is used to handle query parameters in our url. Given a mapping object
 * that defines how to serialize each query param this hook will return the decoded
 * values in an object representing the query params and a setter function to update
 * this object.
 *
 * [paramsSerializerMap] example
 * \{ sort: StringSerializer, page: NumberSerializer \}
 * where the serializer are available in paramsSerializer.js and are used to be able to
 * keep the original query param type trough the process because we need to convert
 * them to string to insert in the url
 *
 * [decodedValues] example
 * \{ sort: '-startDate', page: 1 \}
 *
 * [setQueryParams] example
 * setQueryParams(\{ sort: '' \})
 * NOTE: here the page param will not be changed or overwrites because the default updateType
 * is set to 'replaceIn' which means we don't add a state to the react router history (not a push)
 * and we keep the other existing query params.
 * To see other updateType values please refer to paramsSerializer.js
 *
 * @param paramsSerializerMap - an object mapping query param names to serializers
 */
export function useQueryParams(paramsSerializerMap) {
  const $ = _c(13);
  if (!paramsSerializerMap) {
    throw new Error("You need to define the serializer configuration for the query params you want to use");
  }
  const history = useHistory();
  const location = useLocation();
  let t0;
  if ($[0] !== location.search) {
    t0 = qs.parse(location.search) || {};
    $[0] = location.search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const parsedSearch = t0;
  const [query, setQuery] = React.useState(parsedSearch);
  let t1;
  if ($[2] !== paramsSerializerMap || $[3] !== query) {
    t1 = Object.keys(paramsSerializerMap).reduce((decodedQueryParams, paramName) => ({
      ...decodedQueryParams,
      [paramName]: decodeValue(query[paramName], paramsSerializerMap[paramName as keyof T])
    }), {});
    $[2] = paramsSerializerMap;
    $[3] = query;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const decodedValues = t1;
  let t2;
  if ($[5] !== history || $[6] !== location || $[7] !== paramsSerializerMap || $[8] !== query) {
    t2 = (changes, updateType) => {
      const encodedChanges = encodeQueryParams(changes, paramsSerializerMap);
      const updatedQuery = updateType === UpdateType.Replace ? encodedChanges : {
        ...query,
        ...encodedChanges
      };
      setQuery(filterNullOrEmpty(updatedQuery));
      updateType ? updateUrlSearch(encodedChanges, location, history, updateType) : updateUrlSearch(encodedChanges, location, history);
    };
    $[5] = history;
    $[6] = location;
    $[7] = paramsSerializerMap;
    $[8] = query;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const setQueryParams = t2;
  let t3;
  if ($[10] !== decodedValues || $[11] !== setQueryParams) {
    t3 = [decodedValues, setQueryParams];
    $[10] = decodedValues;
    $[11] = setQueryParams;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export default useQueryParams;